<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import ShipmentProtectionShoppingBanner from '@/pages/shipment-protection-shopping/components/_ShipmentProtectionShoppingBanner.vue'
import ShipmentProtectionShoppingComparison from '@/pages/shipment-protection-shopping/components/_ShipmentProtectionShoppingComparison.vue'
import type { PageContent } from '@/assets/ts/pages/shipment-protection-shipping'
import TabbedFlowSteps from '@/components/TabbedFlowSteps.vue'
import ShipmentProtectionFAQ from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFAQ.vue'
import ExcludedCoverageSection from '@/components/ExcludedCoverageSection.vue'
import ShipmentProtectionFooter from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFooter.vue'
import {
	getFlowIcons,
	createFlowHandlers,
} from '@/lib/shipment-protection-utils'
import ShipmentProtectionShoppingPlaneCard from '@/pages/shipment-protection-shopping/components/_ShipmentProtectionShoppingPlaneCard.vue'
import { smoothScrollToElement, getButtonName } from '@/lib/insurance'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import getRedirectSeoLink from '@/lib/getSeoRedirect'

const props = defineProps<{
	pageContent: PageContent
}>()

const flowIcons = getFlowIcons('shopping')
const { handleButtonClick, handleTabClick } = createFlowHandlers(
	props.pageContent,
)

const comparisonRef = ref<InstanceType<
	typeof ShipmentProtectionShoppingComparison
> | null>(null)

const handleComparisonRef = (theme: string) => {
	smoothScrollToElement(comparisonRef.value?.$el)

	const buttonName = getButtonName(theme)

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShoppingProtection,
		button_name: `Learn More - ${buttonName}`,
	})
}

const breadcrumbs = computed(() => [
	{
		displayName: window.pageContent.breadcrumbs?.home || '',
		categoryId: 'home',
		link: getSiteBaseUrl(),
	},
	{
		displayName: window.pageContent.breadcrumbs?.shopping || '',
		categoryId: 'shopping',
		link: `${getSiteBaseUrl()}/${getRedirectSeoLink('proxyShopping')}/`,
	},
	{
		displayName: window.pageContent.breadcrumbs?.shoppingProtection || '',
		categoryId: 'shoppingProtection',
		link: `${getSiteBaseUrl()}/${getRedirectSeoLink('protectionShopping')}/`,
	},
])

onMounted(() => {
	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.ShoppingProtection,
	})
})
</script>

<template>
	<PageBreadCrumb :breadcrumbs="breadcrumbs" />
	<ShipmentProtectionShoppingBanner :page-content="props.pageContent" />
	<section class="bg-white">
		<ShipmentProtectionShoppingPlaneCard
			:page-content="props.pageContent"
			theme="shopping"
			@scroll-to-top="handleComparisonRef"
		/>
	</section>
	<section class="py-distant-acquaintances">
		<div>
			<ShipmentProtectionShoppingComparison
				ref="comparisonRef"
				:page-content="props.pageContent"
			/>
		</div>
	</section>

	<section
		v-if="props.pageContent.flowContent"
		class="bs-container py-distant-acquaintances"
	>
		<div class="col-span-12">
			<TabbedFlowSteps
				:content="props.pageContent.flowContent"
				:flow-icons="flowIcons"
				@button-click="handleButtonClick"
				@tab-click="handleTabClick"
			/>
		</div>
	</section>

	<ExcludedCoverageSection
		v-if="props.pageContent.excludedCoverage"
		:content="props.pageContent.excludedCoverage"
	/>

	<section class="bg-o-50 py-distant-acquaintances">
		<div class="bs-container">
			<div class="col-span-12">
				<ShipmentProtectionFAQ
					:content="props.pageContent.FAQ"
					theme="shopping"
				/>
			</div>
		</div>
	</section>

	<section class="bs-container py-distant-acquaintances">
		<div class="col-span-12">
			<ShipmentProtectionFooter
				theme="shopping"
				:footer="props.pageContent.footer"
			/>
		</div>
	</section>
</template>
